import { observer } from 'mobx-react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { Button } from '../../../../../components/Button';
import { Caption } from '../../../../../components/Caption';
import { Card } from '../../../../../components/Card';
import { Checkbox } from '../../../../../components/Checkbox';
import { FormItem, FormLabel, FormLayout } from '../../../../../components/Form';
import { Modal } from '../../../../../components/Modal';
import { PhoneInput } from '../../../../../components/PhoneInput';
import { TextField } from '../../../../../components/TextField';
import { Wrapper } from '../../../../../components/Wrapper';
import { Routes } from '../../../../../constants/Routes';
import { useContext } from '../../../../../hooks/useContext';
import { useLocale } from '../../../../../hooks/useLocale';
import { useTransaction } from '../../../../../hooks/useTransaction';
import { SessionStore } from '../../../../../stores/SessionStore';
import { CardInputLayout } from '../../../AccountDetail/components/CardInputLayout';
import { Payjp3dSecureStore } from '../../../store/Payjp3dSecureStore';
import { ProfileStore } from '../../stores/ProfileStore';
import { errorStyle } from '../components/AccountsNameInput';
import { NameInput } from '../components/NameInput';

export const AccountsNewNameInputNormal = observer(() => {
  const { push } = useHistory();
  const lang = useLocale();
  const isEnglish = lang === 'en';
  const store = useMemo(() => new ProfileStore(lang), [lang]);
  const session = useContext(SessionStore.Context);
  const [isRegistering, setIsRegistering] = useState(false);

  // 代理予約対応は当画面のみで判定しているが、他ページにも影響する場合は共通化する
  const params = new URLSearchParams(useLocation().search);
  const isDelegated = params.get('delegated') === 'true' && isEnglish ? true : false;

  // 3Dセキュア認証で失敗した場合の再入力対応
  useEffect(() => {
    store.loadFromSession();
    store.clearSession();
  }, [store]);

  const [doCallback, status] = useTransaction(async (payjpId?: string) => {
    try {
      const params = new URLSearchParams(window.location.search);
      const url = params.get('url');
      const gclid = params.get('gclid');

      // 代理予約（カード入力なし）の場合は、ここで直接ユーザー登録まで完了させる
      if (isDelegated) {
        await store.register();
        // 3DS用の一時データが残っている可能性があるので掃除しておく
        store.clearSession();
        push(url || Routes.BasePath);
        return;
      }

      // 通常フローは 3DS へ遷移するため、入力値を sessionStorage に退避しておく
      store.saveToSession();

      if (payjpId) {
        const finishParams = new URLSearchParams();
        finishParams.set('mode', 'profile');
        if (url) {
          finishParams.set('url', url);
        }
        if (gclid) {
          finishParams.set('gclid', gclid);
        }

        const finishQuery = finishParams.toString();
        const finishUrl = `${window.location.origin}${Routes.Paths.Payjp3dSecureFinish}${
          finishQuery ? `?${finishQuery}` : ''
        }`;
        const backTo = encodeURIComponent(
          `${window.location.pathname}${window.location.search}${window.location.hash}`,
        );
        const payjp3dSecureStore = new Payjp3dSecureStore(payjpId, finishUrl, backTo);
        await payjp3dSecureStore.redirect3dSecure();

        return;
      }

      push(url || Routes.BasePath);
    } finally {
      setIsRegistering(false);
    }
  });

  const [authStateKnown, setAuthStateKnown] = useState(false);
  const [concent, setConcent] = useState(false);

  const onSubmit = useRef<() => void>(doCallback);

  useEffect(() => {
    if (!session.authStateKnown) {
      return;
    }

    const user = session.user?.userEntity;
    if (!user) {
      setAuthStateKnown(true);
      return;
    }

    const url = new URLSearchParams(window.location.search).get('url');
    push(url || Routes.BasePath);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.authStateKnown]);

  useEffect(() => {
    store.setLang(lang);
  }, [lang, store]);

  if (!authStateKnown) {
    return (
      <Modal visible>
        <Card>ユーザー情報を取得しています...</Card>
      </Modal>
    );
  }

  return (
    <Wrapper>
      <Modal visible={isRegistering}>
        <Card>患者プロフィール情報を登録しています...</Card>
      </Modal>
      <Caption type="header" underline align="center">
        {isEnglish ? 'profile registration' : '患者プロフィール登録'}
      </Caption>
      <FormLayout>
        <FormItem>
          <NameInput
            value={store.name || { sei: '', mei: '' }}
            onChange={v => store.setName(v)}
            errors={store.errors}
            lang={lang}
          />
        </FormItem>
        {!isDelegated && (
          <>
            <FormItem>
              <FormLabel required lang={lang}>
                {isEnglish ? 'Phone number' : '電話番号'}
              </FormLabel>
              <PhoneInput
                color="white"
                value={store.phone}
                onChange={v => store.setPhone(v)}
                error={store.errors.some(e => e.field === 'phone')}
                errors={store.errors}
              />
            </FormItem>
            <FormItem>
              <CardInputLayout
                required
                phone={store.phone}
                callback={async t => {
                  setIsRegistering(true);
                  store.setCard({
                    brand: t.brand,
                    id: t.payjpID,
                    expAt: t.expAt,
                    fourDigit: t.fourDigit,
                  });
                  return doCallback(t.payjpID);
                }}
                onSubmit={onSubmit}
                onStatusChange={s => store.cardStatus.setCardTransactionStatus(s)}
                onPreCardValidateChange={v => store.cardStatus.setPreCardValidate(v)}
                isEnglish={isEnglish}
              />
            </FormItem>
          </>
        )}
        {session.user?.isLineAccount && (
          <FormItem>
            <FormLabel>メールアドレス(任意)</FormLabel>
            <TextField type="email" color="white" value={store.mail} onChange={v => store.setMail(v)} />
            <Caption type="small">予約時にGoogleカレンダー招待をお送りします。</Caption>
          </FormItem>
        )}
        <FormItem>
          <Checkbox
            label={isEnglish ? 'I agree with the Terms & Conditions ' : 'TENの利用規約に同意する'}
            checked={concent}
            onChange={v => setConcent(v.target.checked)}
          />
          <div>
            <a style={{ textDecoration: 'underline' }} href="/terms" target="_blank">
              {isEnglish ? 'Confirm Terms of Service' : '利用規約を確認(別タブが開きます)'}
            </a>
          </div>
        </FormItem>
        <div className={errorStyle}>{store.cardStatus.cardTransactionStatus?.error}</div>
        <Button
          type="submit"
          block
          onClick={() => {
            setIsRegistering(true);
            onSubmit.current();
          }}
          disabled={
            status.running ||
            !store.valid ||
            !concent ||
            // 代理予約の場合はカード情報入力不要
            (!isDelegated && store.cardStatus.cardTransactionStatus?.running) ||
            (!isDelegated && store.cardStatus.hasPreCardError) ||
            (!isDelegated && !store.cardStatus.preCardInputCompleted)
          }
        >
          {isEnglish ? 'Register' : '登録する'}
        </Button>
      </FormLayout>
    </Wrapper>
  );
});
